import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { PropsWithChildren, useEffect } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { Drawer } from '../Drawer'
import { Container } from '..'
import { AuthenticationGuard } from '../AuthenticationGuard'
import { useCogfy, useToasts } from '../../hooks'

export type LayoutProps = PropsWithChildren

function Fallback () {
  return (
    <Container>
      Something went wrong
    </Container>
  )
}

export function Layout (props: LayoutProps) {
  const cogfy = useCogfy()
  const workspace = useParams().workspace
  const navigate = useNavigate()
  const toasts = useToasts()

  const getWorkspace = useQuery({
    queryKey: ['getWorkspaceBySlug', { slug: workspace! }] as const,
    queryFn: ({ queryKey, signal }) => cogfy.getWorkspaceBySlug(queryKey[1], { signal }),
    enabled: Boolean(workspace)
  })

  useEffect(() => {
    if (workspace !== 'sign-in') {
      cogfy.getWorkspace = () => workspace ?? null

      return () => {
        cogfy.getWorkspace = null
      }
    }
  }, [workspace])

  useEffect(() => {
    if (cogfy.isNotFound(getWorkspace.error)) {
      toasts.error(`Workspace "${workspace}" not found`)
      navigate('/')
    }
  }, [getWorkspace.error])

  return (
    <AuthenticationGuard>
      <Drawer id="layout-drawer">
        <Sentry.ErrorBoundary fallback={<Fallback />}>
          <div
            className="w-full h-full overflow-auto bg-base-200"
            style={{ scrollbarWidth: 'thin' }}
          >
            <Outlet />
            {props.children}
          </div>
        </Sentry.ErrorBoundary>
      </Drawer>
    </AuthenticationGuard>
  )
}
